import React from "react";
import StickyTableHeader from "../components/utility/StickyTableHeader";
import BasketTotals from "../components/utility/BasketTotals";
import { useCurrency } from "../components/utility/CurrencyContext";
import { useCart } from "./CartContext";

const Basket = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const { getCurrencySymbol, convertPrice } = useCurrency();

  // Same tier logic as Table2.js getPriceForQuantity
  const calculateItemPrice = (quantity, item) => {
    const qty = Number(quantity) || 1;

    let rawPrice;
    if (qty >= 10000 && item.price_10000plus) {
      rawPrice = item.price_10000plus;
    } else if (qty >= 5000 && item.price_5000_9999) {
      rawPrice = item.price_5000_9999;
    } else if (qty >= 1000 && item.price_1000_4999) {
      rawPrice = item.price_1000_4999;
    } else if (qty >= 250 && item.price_250_999) {
      rawPrice = item.price_250_999;
    } else if (item.price_1_249) {
      rawPrice = item.price_1_249;
    } else {
      rawPrice = 0;
    }

    const converted = convertPrice(Number(rawPrice) || 0);
    return converted / 100; // per-piece
  };

  const calculateSubtotal = () =>
    cart.reduce((total, item) => {
      const unit = calculateItemPrice(item.quantity, item);
      return total + unit * item.quantity;
    }, 0);

  return (
    <div className="relative isolate px-6 pt-10 lg:px-8">
      <div className="mx-auto max-w-65rem py-[5rem]">
        <StickyTableHeader />
        <div className="flex flex-wrap gap-10 justify-between">
          <div className="w-full lg:w-[65%] bg-white">
            <h1 className="text-2xl font-bold px-5">Shopping Cart</h1>
            <table className="min-w-full border-collapse bg-white shadow-md mt-6">
              <thead>
                <tr>
                  <th className="py-3 px-4">Action</th>
                  <th className="py-3 px-4">Product</th>
                  <th className="py-3 px-4">Unit Price</th>
                  <th className="py-3 px-4">Quantity</th>
                  <th className="py-3 px-4">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {cart.length ? (
                  cart.map((item) => {
                    const unit = calculateItemPrice(item.quantity, item);
                    const subtotal = item.quantity * unit;
                    return (
                      <tr key={item.id}>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500"
                          >
                            Remove
                          </button>
                        </td>
                        <td className="py-3 px-4">{item.name}</td>
                        <td className="py-3 px-4">
                          {getCurrencySymbol()} {unit.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                updateQuantity(
                                  item.id,
                                  Math.max(1, item.quantity - 1)
                                )
                              }
                              className="px-2 bg-gray-300"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) =>
                                updateQuantity(
                                  item.id,
                                  Math.max(1, parseInt(e.target.value) || 1)
                                )
                              }
                              className="w-12 text-center border rounded"
                            />
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              className="px-2 bg-gray-300"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getCurrencySymbol()} {subtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      Your cart is empty
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="w-full lg:w-[30%]">
            <BasketTotals
              calculateItemPrice={calculateItemPrice}
              cart={cart}
              currencySymbol={getCurrencySymbol()}
              subtotal={calculateSubtotal()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basket;